/**
 * Construit la clé de dédup d'une course par (name, distance).
 *
 * **Source de vérité** : cette fonction doit produire EXACTEMENT le même résultat
 * que la logique inline dans `packages/database/src/services/proposal-domain.service.ts`
 * (autour de la ligne 1072) qui filtre les courses lors de `createRace`. Si la
 * sémantique de dédup évolue côté backend, mettre à jour cette fonction au même
 * commit pour éviter les divergences front/back.
 *
 * **Mode d'emploi recommandé : passer le triplet**
 *
 *   buildRaceMatchKey(name, { runDistance, walkDistance, bikeDistance })
 *
 * Le triplet applique la sémantique `||` (truthy) attendue par le backend : `0`
 * est traité comme "absent" et l'on passe à la distance suivante. C'est ce qui
 * pilote effectivement la dédup côté DB Miles Republic.
 *
 * **Surcharge `number`** : disponible pour les appelants qui ont déjà résolu la
 * distance scalaire. Dans ce cas, la valeur est utilisée telle quelle (pas de
 * fallback). ⚠️ Si l'appelant a fait son propre choix avec `??`, le résultat
 * peut diverger du triplet pour des valeurs `0` ; ne pas pré-calculer en `??`
 * et passer le résultat à cette branche — passer le triplet directement.
 *
 * Cas couverts :
 * - `name` manquant ou vide après trim → `null` (ne pas filtrer)
 * - `distance` null / undefined / NaN / Infinity → `0`
 *
 * @param name Nom de la course (peut être undefined / vide)
 * @param distance Distance scalaire OU triplet { runDistance, walkDistance, bikeDistance }
 * @returns Clé canonique `"<nom-lowercase-trim>::<distance>"` ou `null` si pas de nom
 */
export function buildRaceMatchKey(name, distance) {
    if (!name || typeof name !== 'string')
        return null;
    const normalizedName = name.toLowerCase().trim();
    if (!normalizedName)
        return null;
    let numericDistance;
    if (typeof distance === 'number' && Number.isFinite(distance)) {
        // Surcharge scalaire : valeur utilisée telle quelle (responsabilité de l'appelant).
        numericDistance = distance;
    }
    else if (distance && typeof distance === 'object') {
        // Triplet : sémantique || (truthy) alignée avec le backend.
        // Une valeur de `0` est traitée comme absente et la suivante prend le relais.
        numericDistance =
            (distance.runDistance || 0) ||
                (distance.walkDistance || 0) ||
                (distance.bikeDistance || 0) ||
                0;
    }
    else {
        numericDistance = 0;
    }
    return `${normalizedName}::${numericDistance}`;
}
//# sourceMappingURL=race-matching.js.map